/* global van11yAccessibleTabPanelAria */
import {register} from '@shopify/theme-sections';

const selectors = {
  recentlyViewedWrapper: '[data-recently-viewed-wrapper]',
  recentlyViewedGrid: '[data-recently-viewed-grid]',
  recentlyViewedCard: '[data-product-card-recently-viewed]',
  recommendationsWrapper: '[data-recommendations-wrapper]',
  recommendationsGrid: '[data-recommendations-grid]',
  tabs: '.js-tabs',
  tab: '[data-tab-link]',
  tabActive: '[data-tab-link][aria-selected="true"]',
  skeletonProducts: '[data-products-skeleton]',
};

const classes = {
  hide: 'hide',
};

const sectionType = 'recommended-and-recently-viewed';
const registeredSections = window.Shopify?.theme?.sections?.registered || {};
const isUnregistered = typeof registeredSections[sectionType] === 'undefined';

export default isUnregistered &&
  register(sectionType, {
    onLoad() {
      const container = this.container;
      const skeletonProducts = container.querySelector(selectors.skeletonProducts);

      const activeTabLink = container.querySelector(selectors.tabActive);
      const activeTab = activeTabLink?.dataset.tabLink;

      const initRecommendations = async () => {
        const requestUrl = container.dataset.url;
        if (!requestUrl) return;
        const response = await fetch(requestUrl);
        if (!response.ok) return;

        const data = await response.text();
        const fragment = document.createRange().createContextualFragment(data);
        const responseMarkup = fragment?.querySelector(selectors.recommendationsGrid);
        if (!responseMarkup) return;

        const recommendationsWrapper = container.querySelector(selectors.recommendationsWrapper);
        if (!recommendationsWrapper) return;

        if ('replaceChildren' in HTMLElement.prototype) {
          recommendationsWrapper.replaceChildren(responseMarkup);
        } else {
          recommendationsWrapper.childNodes.forEach((node) => node.remove());
          recommendationsWrapper.appendChild(responseMarkup);
        }

        if (!activeTab || activeTab === 'recommendations') {
          skeletonProducts?.remove();
        }
      };
      initRecommendations();

      const initRecentlyViewed = async () => {
        const productHandle = container.dataset.productHandle;
        const limit = 4;
        const recentlyViewedWrapper = container.querySelector(selectors.recentlyViewedWrapper);
        const recentlyViewedGrid = container.querySelector(selectors.recentlyViewedGrid);

        if (!recentlyViewedGrid) return;

        /**
         * - Retrieve the recent products array from localStorage.
         * - Move the current product to the front of the array.
         * - Trim the length of the array to keep things tidy.
         * - Update the localStorage item with the new array.
         */
        const recentProducts = JSON.parse(localStorage.getItem('recently_viewed_products')) || [];
        const newRecentProducts = recentProducts.filter((item) => item !== productHandle);
        newRecentProducts.unshift(productHandle);
        newRecentProducts.length = Math.min(newRecentProducts.length, limit * 2);
        localStorage.setItem('recently_viewed_products', JSON.stringify(newRecentProducts));

        /**
         * If there are no recent products to show besides the current one,
         * do nothing and hide the section.
         */
        if (newRecentProducts.length === 1) return;

        /**
         * Loop through the recent products array and:
         * - Fetch the product card markup for each item
         * - Append the markup to an array
         * - Stop once the array reaches the `limit`
         * - Join the array into a single string
         */
        const rootUrl = window.theme?.locale?.primary ? '' : window.theme?.locale?.rootUrl;
        const markupArray = [];

        for (const item of newRecentProducts.slice(1)) {
          if (markupArray.length === limit) break;

          try {
            const url = `${rootUrl}/products/${item}?view=recently-viewed`;
            const response = await fetch(url);
            if (!response.ok) continue;
            const productMarkup = await response.text();
            const doc = new DOMParser().parseFromString(productMarkup, 'text/html');
            if (!doc.querySelector(selectors.recentlyViewedCard)) continue;
            markupArray.push(productMarkup);
          } catch (err) {
            window.console.error(`Recently viewed product does not exist: ${item}`);
          }
        }

        if (markupArray.length === 0) return;
        const markup = markupArray.join('');

        /**
         * - Append the product card markup to the slider container
         * - Initialise the product cards then reveal the section
         */
        recentlyViewedGrid.innerHTML = markup;
        recentlyViewedWrapper.classList.remove(classes.hide);

        if (!activeTab || activeTab === 'recently-viewed') {
          skeletonProducts?.remove();
        }
      };
      initRecentlyViewed();

      // Reinitialise tabs in the admin theme customizer when section settings are changed
      if (Shopify.designMode) {
        const tabs = container.querySelector(selectors.tabs);

        // Only reinitialise if the tabs exist and tabs JS hasn't already run (it gives the tabs div an id)
        if (tabs && !tabs.id) {
          van11yAccessibleTabPanelAria(container);
        }
      }
    },
  });
